export enum FeedItemType {
  NeedOnePlayer  = 0,
  PublicGame     = 1,
  FriendActivity = 2,
  UserPost       = 3,
  SharedPost     = 4,
  NewsItem       = 5,
  Tournament     = 6,  
}

export enum PostVisibility {
  FriendsOnly = 0,
  Followers   = 1,
  Standard    = 2,
}

export enum FeedCommentTargetType {
  Post = 0,
  News = 1,
}

export interface FeedAuthor {
  userId: string;
  fullName: string;
  imageUrl?: string;
  influenceScore?: number;
  influenceBadge?: string;
}

export interface OgPreview {
  title?: string;
  description?: string;
  imageUrl?: string;
  siteName?: string;
  url?: string;
}

export interface FeedGame {
  id: string;
  gameName: string;
  sportName: string;
  dateTimeStart: Date;
  currentParticipants: number;
  maxParticipants?: number;
  spotsLeft: number;
  latitude: number;
  longitude: number;
  distanceKm?: number;
}

// ── NEW: Tournament feed item ─────────────────────────────────────────────────
export interface FeedTournament {
  id: string;
  name: string;
  sportName: string;
  sportId: string;
  format: string;
  status: string;
  logoUrl?: string;
  organizerName: string;
  organizerImageUrl?: string;
  startsAt: Date;
  registrationClosesAt: Date;
  registeredTeamsCount: number;
  maxTeams: number;
  isOnline: boolean;
  entryFee?: number;
  prizePool?: string;
  latitude?: number;
  longitude?: number;
  distanceKm?: number;
}

export interface FeedComment {
  id: string;
  content: string;
  author: FeedAuthor;
  createdAt: Date;
  likeCount: number;
  isLikedByCurrentUser: boolean;
  parentCommentId?: string;
  replies: FeedComment[];
}

export interface FeedItem {
  id: string;
  type: FeedItemType;
  createdAt: Date;

  // Post fields
  content?: string;
  visibility?: PostVisibility;
  mediaUrl?: string;
  ogPreview?: OgPreview;

  // Shared post
  sharedByUserId?: string;
  sharedByUserName?: string;
  sharedByUserImage?: string;

  // Author
  author?: FeedAuthor;

  // Game event
  game?: FeedGame;

  // Tournament event 
  tournament?: FeedTournament;

  // Engagement
  likeCount: number;
  commentCount: number;
  shareCount: number;
  isLikedByCurrentUser: boolean;
  isNotRelevantByCurrentUser: boolean;

  topComments: FeedComment[];
}

export interface NewsItem {
  newsId: string;
  title: string;
  description?: string;
  url: string;
  imageUrl?: string;
  source: string;
  publishedAt: Date;
  sportTag: string;
  commentCount: number;
}

export interface InfluenceHistory {
  date: Date;
  score: number;
}

export interface InfluenceProfile {
  score: number;
  badge: string;
  reachKm: number;
  level: string;
  levelKey: string;
  nextLevelScore: number;
  pointsToNextLevel: number;
  totalLikes: number;
  totalNotRelevant: number;
  totalPostsAndGames: number;
  history: InfluenceHistory[];
}

// ── Filter/param types ────────────────────────────────────────────────────────

export interface FeedParams {
  pageNumber: number;
  pageSize: number;
  radiusKm: number;
  sportId?: string;
  showNeedOnePlayer: boolean;
  showPublicGames: boolean;
  showFriendActivity: boolean;
  showUserPosts: boolean;
  showTournaments: boolean;  
  friendsOnly: boolean;
}

export interface FeedPostPageDto {
  postId: string;
  pageNumber: number;
  pageSize: number;
  indexInPage: number;
}

export interface UrgencyParams {
  pageNumber: number;
  pageSize: number;
  radiusKm: number;
  timeWindowHours: number;
  friendsOnly: boolean;
  sportIds?: string[];
}

export interface CreatePostDto {
  content: string;
  mediaUrl?: string;
  visibility: PostVisibility;
}

export interface CreateCommentDto {
  content: string;
  parentCommentId?: string;
  targetType: FeedCommentTargetType;
  feedPostId?: string;
  newsItemId?: string;
}

// ── Influence level helpers ───────────────────────────────────────────────────

export const INFLUENCE_LEVELS = [
  { name: 'Novice',  badge: '🟤', min: 0,    max: 299,      reachKm: 3  },
  { name: 'Rookie',  badge: '🟢', min: 300,  max: 799,      reachKm: 7  },
  { name: 'Player',  badge: '🔵', min: 800,  max: 1999,     reachKm: 15 },
  { name: 'Captain', badge: '🟣', min: 2000, max: 4999,     reachKm: 30 },
  { name: 'Legend',  badge: '🟡', min: 5000, max: Infinity, reachKm: 50 },
] as const;

export function getInfluenceLevel(score: number) {
  return INFLUENCE_LEVELS.find(l => score >= l.min && score <= l.max) ?? INFLUENCE_LEVELS[0];
}

export const DEFAULT_FEED_PARAMS: FeedParams = {
  pageNumber: 1,
  pageSize: 20,
  radiusKm: 10,
  showNeedOnePlayer: true,
  showPublicGames: true,
  showFriendActivity: true,
  showUserPosts: true,
  showTournaments: true,   // on by default
  friendsOnly: false,
};

export const DEFAULT_URGENCY_PARAMS: UrgencyParams = {
  pageNumber: 1,
  pageSize: 20,
  radiusKm: 10,
  timeWindowHours: 6,
  friendsOnly: false,
};